///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Playback engine for automated camera legs. A "fly to saved view" is a
/// one-leg tour; multi-stop tours (phase 5) will sequence legs through the
/// same code path.
///
/// The player never bypasses EarthCameraState: it writes poses through
/// setParamsImmediate + updateProjection every frame, so redux camera sync
/// and altitude clamps keep working (the writes are "auto" motion, so the
/// crosshair stays hidden: there is nothing to aim during a flight). User
/// input cancels playback instantly with no snap because the automated
/// motion and the gesture handlers share the same live camera state.
///

import { CameraParams, type EarthCameraState } from "@/earthbrowser/activities/EarthActivity/EarthCameraState"
import { wgs } from "@/earthbrowser/utils/geographic"
import { spherical } from "@/earthbrowser/utils/spherical"
import { trig } from "@stovokor/math/math"

/** Travel time bounds when the caller doesn't specify one (seconds) */
const cMinTravelSeconds = 1.6
const cMaxTravelSeconds = 7

/** Extra travel seconds per radian of great-circle distance */
const cTravelSecondsPerRadian = 2.2

/**
 * Altitude-arc gain: peak altitude for a leg is roughly
 * angularDistance * earthRadius * cArcGain, so long hops zoom out far
 * enough to see the path while short hops stay put (arc becomes a no-op).
 */
const cArcGain = 0.5

function easeInOut(t: number): number {
	return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
}

function lerp(a: number, b: number, t: number): number {
	return a + (b - a) * t
}

/** Great-circle angular distance (radians) between two camera poses */
function angularDistance(a: CameraParams, b: CameraParams): number {
	const va = new spherical(1, a.theta, a.phi).cartesian
	const vb = new spherical(1, b.theta, b.phi).cartesian
	const dot = Math.max(-1, Math.min(1, va.x * vb.x + va.y * vb.y + va.z * vb.z))
	return Math.acos(dot)
}

export class TourPlayer {
	private cameraState: EarthCameraState

	private from?: CameraParams
	private to?: CameraParams
	private arcHeight = 0
	private duration = 1
	private elapsed = 0
	private active = false

	/** Fired when a leg finishes on its own (not when stopped by input) */
	public onLegComplete?: () => void

	constructor(cameraState: EarthCameraState) {
		this.cameraState = cameraState
	}

	public get playing(): boolean {
		return this.active
	}

	/**
	 * Begin a leg from the current camera pose to the target pose.
	 * Theta and bearing are unwrapped to the shortest path (same as
	 * flyToLocation), and long hops get a bell-curve altitude arc for the
	 * zoom-out-fly-zoom-in move.
	 */
	public startLeg(target: CameraParams, travelSeconds?: number): void {
		const from = this.cameraState.getCurrentParams()

		// Shortest-path theta unwrap
		let theta = target.theta
		while (from.theta - theta > trig.pi) theta += trig.two_pi
		while (from.theta - theta < -trig.pi) theta -= trig.two_pi

		// Shortest-path bearing unwrap
		let bearing = target.bearing
		while (from.bearing - bearing > trig.pi) bearing += trig.two_pi
		while (from.bearing - bearing < -trig.pi) bearing -= trig.two_pi

		const to = new CameraParams(target.radius, theta, target.phi, target.tilt, bearing)
		const angDist = angularDistance(from, to)

		// Altitude arc: rise to see the path on long hops, no-op on short ones
		const peakAltitude = angDist * wgs.a * cArcGain
		const midAltitude = (from.radius + to.radius) * 0.5 - wgs.a
		this.arcHeight = Math.max(0, peakAltitude - midAltitude)

		this.from = from
		this.to = to
		this.duration = travelSeconds ?? Math.min(
			cMaxTravelSeconds,
			cMinTravelSeconds + angDist * cTravelSecondsPerRadian
		)
		this.duration = Math.max(0.1, this.duration)
		this.elapsed = 0
		this.active = true
	}

	/** Stop playback where it is (used when user input takes over) */
	public stop(): void {
		if (!this.active) return
		this.active = false
		this.cameraState.saveCameraState()
	}

	/** Per-frame tick; dt in seconds */
	public update(dt: number): void {
		if (!this.active || !this.from || !this.to) return

		this.elapsed += dt
		const t = Math.min(1, this.elapsed / this.duration)
		const e = easeInOut(t)

		const radius = this.cameraState.elevationRange.clamp(
			lerp(this.from.radius, this.to.radius, e) + this.arcHeight * Math.sin(Math.PI * t)
		)

		this.cameraState.setParamsImmediate(new CameraParams(
			radius,
			lerp(this.from.theta, this.to.theta, e),
			lerp(this.from.phi, this.to.phi, e),
			lerp(this.from.tilt, this.to.tilt, e),
			lerp(this.from.bearing, this.to.bearing, e)
		))

		if (t >= 1) {
			this.active = false
			this.cameraState.saveCameraState()
			this.onLegComplete?.()
		}
	}
}
